// =============================================================================
// FILE: packages/renderer/src/hooks/useLicense.ts
// PURPOSE: Fetches and manages the license gate state from the main process.
//          Returns the live status and an async `verify` function.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { call } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { MASTER_LICENSE_TIER } from '@bim/shared/config/masterKey.config.js';
import { useAuthStore } from '../stores/auth.store.js';
import { useEliteStore } from '../stores/elite.store.js';

/** useLicense interface */
export interface LicenseStatus {
  status:        'trial' | 'expired' | 'verified';
  daysRemaining: number;
  trialStartIso: string;
  bicsiId:       string | null;
  readOnly:      boolean;
  tier?:         'technician' | 'contractor' | 'designer';
}

interface UseLicenseReturn {
  licenseStatus:  LicenseStatus | null;
  isLoading:      boolean;
  /** Submits a BICSI ID. Returns { ok, error? }. On success the hook re-fetches. */
  verify:         (bicsiId: string) => Promise<{ ok: boolean; error?: string }>;
}

/** Master admin — permanent Designer tier, all gates bypassed. */
const MASTER_ADMIN_STATUS: LicenseStatus = {
  status:        'verified',
  daysRemaining: 99999,
  trialStartIso: new Date().toISOString(),
  bicsiId:       'MASTER-ADMIN',
  readOnly:      false,
  tier:          MASTER_LICENSE_TIER,
};

export function useLicense(): UseLicenseReturn {
  const user = useAuthStore((s) => s.user);
  const eliteVerified = useEliteStore((s) => s.verified);
  const eliteBicsiId  = useEliteStore((s) => s.bicsiId);
  const eliteDays     = useEliteStore((s) => s.daysRemaining);
  const [status, setStatus]   = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    // ── Master Admin: bypass all license checks immediately ────────────────
    if (user?.role === 'Founder' || user?.roles?.includes('Founder')) {
      setStatus(MASTER_ADMIN_STATUS);
      setLoading(false);
      return;
    }

    // ── Identity Transformation: verified RCDD → Gold Enterprise Edition ───
    if (eliteVerified && eliteBicsiId) {
      setStatus({
        status:        'verified',
        daysRemaining: eliteDays,
        trialStartIso: new Date().toISOString(),
        bicsiId:       eliteBicsiId,
        readOnly:      false,
        tier:          'designer',
      });
      setLoading(false);
      return;
    }

    // ── Default: every trial user starts on the Technician Edition ─────────
    const TECHNICIAN_TRIAL: LicenseStatus = {
      status:        'trial',
      daysRemaining: 100,
      trialStartIso: new Date().toISOString(),
      bicsiId:       null,
      readOnly:      false,
      tier:          'technician',
    };

    try {
      const res = await call<undefined, { ok: boolean; data?: LicenseStatus }>(IPC.LICENSE_STATUS, undefined);
      if (res?.ok && res.data) {
        setStatus(res.data);
      } else {
        // IPC not available (web preview) — Technician trial by default
        setStatus(TECHNICIAN_TRIAL);
      }
    } catch {
      setStatus(TECHNICIAN_TRIAL);
    } finally {
      setLoading(false);
    }
  }, [user, eliteVerified, eliteBicsiId, eliteDays]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const verify = useCallback(async (rawId: string) => {
    try {
      const res = await call<string, { ok: boolean; error?: string }>(IPC.LICENSE_VERIFY, rawId);
      if (res?.ok) {
        await fetchStatus(); // re-fetch updated status
        return { ok: true };
      }
      return { ok: false, error: res?.error ?? 'Verification failed.' };
    } catch {
      return { ok: false, error: 'Could not reach the license service.' };
    }
  }, [fetchStatus]);

  return { licenseStatus: status, isLoading: loading, verify };
}
